import type { GlobalProductSuiteCredentialsGetResponse } from "@openanalyst-ai/sdk/v2/client"

export type ProductSuiteHandleInput = {
  handle: string
  handleDisplay?: string | null
  accessRole?: string | null
  enabled?: boolean
  disabled?: boolean
  status?: string | null
  state?: string | null
}
export type ProductSuiteHandle = ProductSuiteHandleInput & { hasPat?: boolean }
export type ProductSuiteCredentials = Omit<GlobalProductSuiteCredentialsGetResponse, "handles"> & {
  projectPath?: string
  projectHandle?: string
  projectHandles?: Record<string, string>
  handles: ProductSuiteHandle[]
}
type ProductSuiteCredentialsPatch = {
  baseURL?: string
  handle?: string
  handles?: Array<string | ProductSuiteHandleInput>
  projectPath?: string
  token?: string
  pat?: string | null
  clearPat?: boolean
  clearProjectHandle?: boolean
  removeHandle?: string
}
type ProductSuiteCredentialsEndpoint = {
  get: () => Promise<{ data: ProductSuiteCredentials }>
  update: (parameters?: ProductSuiteCredentialsPatch) => Promise<{ data: ProductSuiteCredentials }>
}
type ProductSuiteCredentialsClient = {
  global?: {
    productSuite?: {
      credentials?: ProductSuiteCredentialsEndpoint
    }
  }
  productSuite?: {
    credentials?: ProductSuiteCredentialsEndpoint
  }
}

export function productSuiteCredentialsClient(client: unknown) {
  const sdk = client as ProductSuiteCredentialsClient
  const credentials = sdk.global?.productSuite?.credentials ?? sdk.productSuite?.credentials
  if (!credentials) throw new Error("Product Suite credentials API is not available. Restart OpenAnalyst after rebuilding.")
  return credentials
}

export function productSuiteHandleOptions(status: ProductSuiteCredentials | undefined) {
  const seen = new Set<string>()
  // Account-scoped pickup: only surface handles that belong to the current signed-in
  // account. `status.handles` is the authoritative account list (fetched from the account's
  // JWT via /v2/handles) and `status.handle` is that account's active handle. Project
  // bindings (`projectHandle` / `projectHandles`) are deliberately NOT merged in — a handle
  // that is bound to a project but is not owned/shared by THIS account must never appear
  // (and must not power the project or its MCP; see productSuiteActiveHandle). This is the
  // difference between account-related and project-related handle + MCP pickup.
  return [
    ...(status?.handles ?? []),
    ...(status?.handle && !(status.handles ?? []).some((item) => item.handle === status.handle)
      ? [{ handle: status.handle, hasPat: status.hasPat }]
      : []),
  ].filter((item): item is ProductSuiteHandle => {
    const handle = item.handle.trim()
    if (!handle || seen.has(handle)) return false
    seen.add(handle)
    return true
  })
}

const inactiveHandleStates = new Set(["archived", "deleted", "disabled", "inactive", "removed", "suspended"])

function productSuiteHandleRecord(item: ProductSuiteHandle) {
  return item as ProductSuiteHandle & Record<string, unknown>
}

export function productSuiteHandleEnabled(item: ProductSuiteHandle) {
  const record = productSuiteHandleRecord(item)
  if (record.disabled === true) return false
  if (record.isDisabled === true) return false
  if (record.enabled === false) return false
  const status = typeof record.status === "string" ? record.status.trim().toLowerCase() : ""
  if (status && inactiveHandleStates.has(status)) return false
  const state = typeof record.state === "string" ? record.state.trim().toLowerCase() : ""
  if (state && inactiveHandleStates.has(state)) return false
  return true
}

export function productSuiteActiveHandleOptions(status: ProductSuiteCredentials | undefined) {
  return productSuiteHandleOptions(status).filter(productSuiteHandleEnabled)
}

export function productSuiteProjectKey(projectPath: string | null | undefined) {
  return projectPath?.trim() || ""
}

export function productSuiteProjectHandle(status: ProductSuiteCredentials | undefined, projectPath?: string | null) {
  const key = productSuiteProjectKey(projectPath)
  const mapped = key ? status?.projectHandles?.[key]?.trim() : undefined
  const statusProjectKey = productSuiteProjectKey(status?.projectPath)
  const statusProjectHandle = key && key === statusProjectKey ? status?.projectHandle?.trim() : undefined
  return mapped || statusProjectHandle || ""
}

export function productSuiteActiveHandle(status: ProductSuiteCredentials | undefined, projectPath?: string | null) {
  // Only an account handle may be active / power the project's MCP. A project bound to a
  // handle that is not in this account (a stale or cross-account binding) is ignored and
  // falls back to the account's active handle, then the first account handle.
  const options = productSuiteHandleOptions(status)
  const isAccountHandle = (handle: string) => !!handle && options.some((item) => item.handle === handle)
  const project = productSuiteProjectHandle(status, projectPath)

  /**
   * AN EMPTY ACCOUNT LIST IS "WE DO NOT KNOW YET", NOT "YOUR BINDING IS WRONG".
   *
   * The account-scoping below is right: a handle this account does not own must not power the
   * project or its MCP. But it decided that by asking "is the binding in `status.handles`?" — and
   * `status.handles` is fetched from `/v2/handles`, so it is EMPTY until that call lands. The
   * moment right after you connect is exactly when it is empty.
   *
   * So on connect, a perfectly good binding failed `isAccountHandle`, fell past every branch, and
   * came out as `""` — no handle, no MCP, no plugin-app tools. Removing the handle and binding it
   * again "fixed" it only because by then the list had loaded. That is the reported bug, and the
   * workaround was the diagnosis: nothing was wrong with the binding, we just judged it before we
   * had the evidence to.
   *
   * With no list, keep what the project told us. A cross-account binding is rejected on the
   * evidence of a list that HAS loaded and genuinely lacks the handle — not on the silence of one
   * that has not loaded yet. The worst case here is one render with the previously-bound handle
   * before the list arrives and corrects it; the worst case before was the user re-binding by hand.
   */
  if (project && !options.length) return project

  if (isAccountHandle(project)) return project
  const active = status?.handle?.trim() ?? ""
  if (isAccountHandle(active)) return active
  return options[0]?.handle || ""
}

export function productSuiteSelectedHandle(status: ProductSuiteCredentials | undefined, projectPath?: string | null) {
  const active = productSuiteActiveHandle(status, projectPath)
  return productSuiteHandleOptions(status).find((item) => item.handle === active)
}

export function productSuiteActiveSelectableHandle(status: ProductSuiteCredentials | undefined, projectPath?: string | null) {
  const handles = productSuiteActiveHandleOptions(status)
  const projectHandle = productSuiteProjectHandle(status, projectPath)
  const statusHandle = status?.handle?.trim()
  return handles.find((item) => item.handle === projectHandle)?.handle || handles.find((item) => item.handle === statusHandle)?.handle || handles[0]?.handle || ""
}

export function productSuiteSelectedActiveHandle(status: ProductSuiteCredentials | undefined, projectPath?: string | null) {
  const active = productSuiteActiveSelectableHandle(status, projectPath)
  return productSuiteActiveHandleOptions(status).find((item) => item.handle === active)
}

export function productSuiteHandleLabel(item: ProductSuiteHandle) {
  const display = item.handleDisplay?.trim()
  if (!display || display === item.handle) return item.handle
  return `${display} (${item.handle})`
}

export function productSuiteHandleHost(handle: string | null | undefined) {
  const value = handle?.trim()
  if (!value) return ""
  return value.endsWith(".openanalyst.com") ? value : `${value}.openanalyst.com`
}

export function productSuiteHandleMcpUrl(handle: string | null | undefined) {
  const host = productSuiteHandleHost(handle)
  return host ? `https://${host.replace(/\.mcp\.openanalyst\.com$/i, ".openanalyst.com").replace(/\.openanalyst\.com$/i, ".mcp.openanalyst.com")}/mcp` : ""
}

export function productSuiteHandleMcpBaseUrl(handle: string | null | undefined) {
  return productSuiteHandleMcpUrl(handle).replace(/\/mcp$/i, "")
}

export function productSuiteConnectedHandles(status: ProductSuiteCredentials | undefined) {
  return new Set(status?.connectedHandles ?? [])
}

// Parse a bare handle out of what the user pastes in the "Add handle" field: a bare
// handle ("reviews"), a handle host ("reviews.openanalyst.com"), a handle MCP host/URL
// ("reviews.mcp.openanalyst.com", "https://reviews.mcp.openanalyst.com/mcp"). Returns "" if it does not
// look like a handle so the UI can reject it before saving a PAT against nonsense.
export function productSuiteHandleFromInput(value: string | null | undefined): string {
  const raw = (value ?? "").trim().toLowerCase()
  if (!raw) return ""
  const host = (raw.replace(/^[a-z][a-z0-9+.-]*:\/\//, "").split("/")[0] ?? "").split("?")[0] ?? ""
  const handle = (host || raw).replace(/\.mcp\.openanalyst\.com$/i, "").replace(/\.openanalyst\.com$/i, "").trim()
  return /^[a-z0-9][a-z0-9-]*$/.test(handle) ? handle : ""
}

// Is this handle locked to any project (account-wide projectHandles map or the active
// project's projectHandle)? Remove is blocked for bound handles so a project never
// loses the identity it is pinned to.
export function productSuiteHandleBoundToProject(status: ProductSuiteCredentials | undefined, handle: string): boolean {
  const target = handle.trim()
  if (!target) return false
  const map = status?.projectHandles ?? {}
  if (Object.values(map).some((value) => (value ?? "").trim() === target)) return true
  return (status?.projectHandle ?? "").trim() === target
}

const ownerRoles = new Set(["owner", "admin", "account_owner", "account-owner", "primary"])
const sharedRoles = new Set(["member", "shared", "collaborator", "guest", "viewer", "editor", "read", "write"])

// Human label for a handle's access role: "Owner" for the account's own handles,
// "Shared" for handles shared into the account, else the raw role. Empty when unknown.
export function productSuiteHandleAccessLabel(item: ProductSuiteHandle): string {
  const role = item.accessRole?.trim().toLowerCase() ?? ""
  if (!role) return ""
  if (ownerRoles.has(role)) return "Owner"
  if (sharedRoles.has(role)) return "Shared"
  return item.accessRole?.trim() ?? ""
}

export function productSuiteHandleIsShared(item: ProductSuiteHandle): boolean {
  return productSuiteHandleAccessLabel(item) === "Shared"
}

export const PRODUCT_SUITE_DEFAULT_BASE_URL = "https://ai.openanalyst.com"
export const PRODUCT_SUITE_APP_BASE_URL = "https://app.openanalyst.com"
const PRODUCT_SUITE_ACCESS_TOKEN_REQUIRED =
  "Product Suite manual access requires a 10x access_token. Paste the 10x access_token, not the id_token."

function jsonObject(value: unknown) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

function stringValue(value: unknown) {
  if (typeof value !== "string") return null
  const next = value.trim()
  return next || null
}

function stringArray(value: unknown) {
  if (!Array.isArray(value)) return []
  return value.filter((item): item is string => typeof item === "string").map((item) => item.trim()).filter(Boolean)
}

function decodeProductSuiteJwtClaims(token: string) {
  const payload = token.trim().split(".")[1]
  if (!payload) return null
  try {
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/")
    return jsonObject(JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "="))))
  } catch {
    return null
  }
}

export function productSuiteJwtTokenUse(token: string) {
  return stringValue(decodeProductSuiteJwtClaims(token)?.token_use)
}

function productSuiteJwtSubject(token: string) {
  const claims = decodeProductSuiteJwtClaims(token)
  return stringValue(claims?.sub) ?? stringValue(claims?.username)
}

function productSuiteJwtEmail(token: string) {
  return stringValue(decodeProductSuiteJwtClaims(token)?.email)?.toLowerCase()
}

export function productSuiteJwtSameSubject(left: string, right: string) {
  const leftSubject = productSuiteJwtSubject(left)
  const rightSubject = productSuiteJwtSubject(right)
  if (leftSubject && rightSubject) return leftSubject === rightSubject
  const leftEmail = productSuiteJwtEmail(left)
  const rightEmail = productSuiteJwtEmail(right)
  return !!leftEmail && leftEmail === rightEmail
}

export function productSuiteTokenFromPayload(payload: unknown) {
  const root = jsonObject(payload)
  const openAnalyst = jsonObject(root?.openAnalyst) ?? jsonObject(root?.open_analyst)
  return (
    stringValue(root?.accessToken) ??
    stringValue(root?.access_token) ??
    stringValue(openAnalyst?.accessToken) ??
    stringValue(openAnalyst?.access_token) ??
    null
  )
}

export function productSuiteIdTokenFromPayload(payload: unknown) {
  const root = jsonObject(payload)
  const openAnalyst = jsonObject(root?.openAnalyst) ?? jsonObject(root?.open_analyst)
  return (
    stringValue(root?.idToken) ??
    stringValue(root?.id_token) ??
    stringValue(openAnalyst?.idToken) ??
    stringValue(openAnalyst?.id_token) ??
    null
  )
}

export function productSuiteManualAccessTokenFromInput(input: { token: string; refreshPayload?: unknown }) {
  const token = stringValue(input.token)
  if (!token) return null
  if (productSuiteJwtTokenUse(token) !== "id") return token
  const refreshedAccessToken = productSuiteTokenFromPayload(input.refreshPayload)
  const refreshedIdToken = productSuiteIdTokenFromPayload(input.refreshPayload)
  if (!refreshedAccessToken || productSuiteJwtTokenUse(refreshedAccessToken) === "id") return null
  if (!refreshedIdToken || !productSuiteJwtSameSubject(token, refreshedIdToken)) return null
  return refreshedAccessToken
}

export function productSuiteUserEmailFromPayload(payload: unknown) {
  const root = jsonObject(payload)
  const openAnalyst = jsonObject(root?.openAnalyst) ?? jsonObject(root?.open_analyst)
  return (
    stringValue(root?.userEmail) ??
    stringValue(root?.user_email) ??
    stringValue(openAnalyst?.userEmail) ??
    stringValue(openAnalyst?.user_email) ??
    null
  )
}

export function productSuiteHandlesFromPayloads(input: { me?: unknown; handlesPayload?: unknown }) {
  const seen = new Set<string>()
  const me = jsonObject(input.me)
  const handlesPayload = jsonObject(input.handlesPayload)
  const meTenant = jsonObject(me?.tenant)
  const meAccount = jsonObject(me?.account)
  const directHandle = jsonObject(handlesPayload?.handle)
  const defaultHandle =
    stringValue(me?.defaultHandle) ??
    stringValue(me?.default_handle) ??
    stringValue(meTenant?.handle) ??
    stringValue(meTenant?.name) ??
    stringValue(meAccount?.defaultHandle) ??
    stringValue(meAccount?.default_handle)

  const add = (handle: string | null, handleDisplay?: string | null, accessRole?: string | null) => {
    if (!handle || seen.has(handle)) return []
    seen.add(handle)
    return [{ handle, handleDisplay, accessRole }]
  }

  return {
    handles: [
      ...add(
        stringValue(meTenant?.handle) ?? stringValue(meTenant?.name),
        stringValue(meTenant?.handleDisplay) ?? stringValue(meTenant?.handle_display) ?? stringValue(meTenant?.displayName),
        stringValue(meTenant?.accessRole) ?? stringValue(meTenant?.access_role) ?? stringValue(meTenant?.role),
      ),
      ...add(
        directHandle
          ? stringValue(directHandle.handle) ?? stringValue(directHandle.name)
          : stringValue(handlesPayload?.handle),
        directHandle
          ? stringValue(directHandle.handleDisplay) ??
              stringValue(directHandle.handle_display) ??
              stringValue(directHandle.displayName)
          : null,
        directHandle
          ? stringValue(directHandle.accessRole) ??
              stringValue(directHandle.access_role) ??
              stringValue(directHandle.role)
          : null,
      ),
      ...(
        (Array.isArray(handlesPayload?.handles) ? handlesPayload.handles : null) ??
        (Array.isArray(handlesPayload?.items) ? handlesPayload.items : [])
      ).flatMap((item) => {
        const value = jsonObject(item)
        const enabled = typeof value?.enabled === "boolean" ? value.enabled : undefined
        const disabled =
          typeof value?.disabled === "boolean"
            ? value.disabled
            : typeof value?.isDisabled === "boolean"
              ? value.isDisabled
              : undefined
        return add(
          stringValue(value?.handle) ?? stringValue(value?.name),
          stringValue(value?.handleDisplay) ?? stringValue(value?.handle_display) ?? stringValue(value?.displayName),
          stringValue(value?.accessRole) ?? stringValue(value?.access_role) ?? stringValue(value?.role),
        ).map((handle) => ({
          ...handle,
          ...(enabled !== undefined ? { enabled } : {}),
          ...(disabled !== undefined ? { disabled } : {}),
          ...(stringValue(value?.status) ? { status: stringValue(value?.status) } : {}),
          ...(stringValue(value?.state) ? { state: stringValue(value?.state) } : {}),
        }))
      }),
      ...stringArray(handlesPayload?.handles).flatMap((handle) => add(handle)),
      ...stringArray(handlesPayload?.items).flatMap((handle) => add(handle)),
      ...add(defaultHandle),
    ] satisfies ProductSuiteHandleInput[],
    defaultHandle,
  }
}

export async function fetchProductSuiteHandlesFromJwt(input: {
  token: string
  baseURL?: string | null
  fetch?: typeof fetch
}) {
  const fetchImpl = input.fetch ?? fetch
  const baseURL = input.baseURL?.trim() || PRODUCT_SUITE_DEFAULT_BASE_URL
  if (productSuiteJwtTokenUse(input.token) === "id") throw new Error(PRODUCT_SUITE_ACCESS_TOKEN_REQUIRED)
  const [me, handlesPayload] = await Promise.all(
    ["/v2/account/openanalyst/me", "/v2/handles"].map(async (item) => {
      const response = await fetchImpl(new URL(item, baseURL), {
        headers: { Authorization: `Bearer ${input.token}` },
      })
      if (!response.ok) {
        throw new Error(
          response.status === 401
            ? `Product Suite handle refresh failed: ${response.status}. Reauthenticate or paste a fresh 10x access_token.`
            : `Product Suite handle refresh failed: ${response.status}`,
        )
      }
      return response.json() as Promise<unknown>
    }),
  )
  return { baseURL, ...productSuiteHandlesFromPayloads({ me, handlesPayload }) }
}
